import { logError } from '@/lib/sentry.lib';

import { StrictMode, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { RouterProvider } from 'react-router/dom';

import { Router } from '@/router';
import {
    PersistQueryClientProvider,
    ReactQueryDevtools,
    onlineManager,
    persistOptions,
    queryClient,
} from '@repo/lib/react-query';
import { TrpcProvider } from '@repo/lib/trpc-context';
import { translate } from '@repo/phrase';
import { createTrpc } from '@repo/trpc-client';

import { OnlineUpdate } from '@/components/online-update';

import { getServiceEndpoint } from '@/api/helpers.api';
import { getAppConfig } from '@/utils/window.utils';

import './styles.scss';

export const App = () => {
    const [trpc] = useState(() =>
        createTrpc({ baseUrl: getServiceEndpoint(getAppConfig().api.services.orderTaking, 'trpc') })
    );

    return (
        <StrictMode>
            <ErrorBoundary
                onError={
                    /* v8 ignore start */ (error) => {
                        logError(error);
                    }
                    /* v8 ignore end */
                }
                fallback={<p>{translate('app_error_fallback_message')}</p>}
            >
                <TrpcProvider client={trpc}>
                    <PersistQueryClientProvider
                        client={queryClient}
                        persistOptions={persistOptions}
                        onSuccess={() => {
                            queryClient
                                .invalidateQueries({
                                    predicate: (query) => query.meta?.persist === true,
                                })
                                .catch((err: unknown) => {
                                    /* v8 ignore next */
                                    logError(err);
                                });

                            if (onlineManager.isOnline()) {
                                queryClient.resumePausedMutations().catch((err: unknown) => {
                                    /* v8 ignore next */
                                    logError(err);
                                });
                            }
                        }}
                    >
                        <ReactQueryDevtools initialIsOpen={false} />
                        <OnlineUpdate />
                        <RouterProvider router={Router} />
                    </PersistQueryClientProvider>
                </TrpcProvider>
            </ErrorBoundary>
        </StrictMode>
    );
};
